import { getApiUrl } from '../utils/config';

interface SocialLimitResponse {
  allowed: boolean;
  current_count: number;
  limit: number;
  remaining: number;
  unlimited: boolean;
  reset_date?: string;
  usage_percentage?: number;
}

interface StorageLimitResponse extends SocialLimitResponse {
  current_usage_gb: number;
  limit_gb: number;
  remaining_gb: number;
}

interface AnalyticsHistoryResponse {
  allowed: boolean;
  retention_days: number;
  cutoff_date: string;
  unlimited: boolean;
}

interface AllSocialLimitsResponse {
  connected_accounts: SocialLimitResponse;
  monthly_posts: SocialLimitResponse;
  scheduled_queue: SocialLimitResponse;
  team_members: SocialLimitResponse;
  storage: StorageLimitResponse;
  analytics_history: AnalyticsHistoryResponse;
}

interface UpgradeSuggestion {
  feature: string;
  usage_percentage: number;
  current: number;
  limit: number;
  priority: 'high' | 'medium' | 'low';
}

interface UpgradeSuggestionsResponse {
  needs_upgrade: boolean;
  suggestions: UpgradeSuggestion[];
  recommended_tier: string;
}

interface FileUploadCheckResponse {
  allowed: boolean;
  file_size_mb: number;
  storage_info: StorageLimitResponse;
}

class SocialLimitsService {
  private getAuthHeaders() {
    const token = localStorage.getItem('access_token');
    const clientId = localStorage.getItem('current_client_id');
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    if (clientId) {
      headers['X-Client-ID'] = clientId;
    }
    return headers;
  }

  async checkConnectedAccountsLimit(): Promise<SocialLimitResponse> {
    const response = await fetch(`${getApiUrl()}/api/v1/social-limits/connected-accounts`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to check connected accounts limit');
    }

    return response.json();
  }

  async checkMonthlyPostsLimit(): Promise<SocialLimitResponse> {
    const response = await fetch(`${getApiUrl()}/api/v1/social-limits/monthly-posts`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to check monthly content limit');
    }

    return response.json();
  }

  async trackPostPublish(): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${getApiUrl()}/api/v1/social-limits/monthly-posts/track`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to track post publish');
    }

    return response.json();
  }

  async checkScheduledQueueLimit(): Promise<SocialLimitResponse> {
    const response = await fetch(`${getApiUrl()}/api/v1/social-limits/scheduled-queue`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to check scheduled queue limit');
    }

    return response.json();
  }

  async checkTeamMembersLimit(): Promise<SocialLimitResponse> {
    const response = await fetch(`${getApiUrl()}/api/v1/social-limits/team-members`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to check team members limit');
    }

    return response.json();
  }

  async checkStorageLimit(): Promise<StorageLimitResponse> {
    const response = await fetch(`${getApiUrl()}/api/v1/social-limits/storage`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to check storage limit');
    }

    return response.json();
  }

  async checkAnalyticsHistoryLimit(): Promise<AnalyticsHistoryResponse> {
    const response = await fetch(`${getApiUrl()}/api/v1/social-limits/analytics-history`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to check analytics history limit');
    }

    return response.json();
  }

  async getAllSocialLimits(): Promise<AllSocialLimitsResponse> {
    const response = await fetch(`${getApiUrl()}/api/v1/social-limits/all`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to get all social limits');
    }

    return response.json();
  }

  async getUpgradeSuggestions(): Promise<UpgradeSuggestionsResponse> {
    const response = await fetch(`${getApiUrl()}/api/v1/social-limits/upgrade-suggestions`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to get upgrade suggestions');
    }

    return response.json();
  }

  async checkFileUploadLimit(fileSizeBytes: number): Promise<FileUploadCheckResponse> {
    const response = await fetch(`${getApiUrl()}/api/v1/social-limits/check-file-upload`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ file_size_bytes: fileSizeBytes }),
    });

    if (!response.ok) {
      throw new Error('Failed to check file upload limit');
    }

    return response.json();
  }

  // Convenience methods for simple boolean checks
  async canConnectAccount(): Promise<{ allowed: boolean }> {
    const response = await fetch(`${getApiUrl()}/api/v1/social-limits/can-connect-account`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to check if can connect account');
    }

    return response.json();
  }

  async canPublishPost(): Promise<{ allowed: boolean }> {
    const response = await fetch(`${getApiUrl()}/api/v1/social-limits/can-publish-post`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to check if can publish content');
    }

    return response.json();
  }

  async canSchedulePost(): Promise<{ allowed: boolean }> {
    const response = await fetch(`${getApiUrl()}/api/v1/social-limits/can-schedule-post`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to check if can schedule post');
    }

    return response.json();
  }

  async canAddTeamMember(): Promise<{ allowed: boolean }> {
    const response = await fetch(`${getApiUrl()}/api/v1/social-limits/can-add-team-member`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to check if can add team member');
    }

    return response.json();
  }
}

export default new SocialLimitsService();